import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import SEO from "@/components/SEO.jsx";
import { SEO_KEYWORDS } from "@/seo/seoData";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Globe,
  Code,
  Smartphone,
  Zap,
  Shield,
  TrendingUp,
  CheckCircle,
  ChevronDown,
  Layers,
} from "lucide-react";

const capabilities = [
  {
    icon: Shield,
    title: "Website Updates",
    description:
      "Core updates to keep the website current, stable, and functioning properly.",
  },
  {
    icon: Layers,
    title: "Security Monitoring",
    description:
      "Checks and support to reduce risk and keep the site protected.",
  },
  {
    icon: Code,
    title: "Bug Fixes",
    description:
      "Ongoing issue resolution for broken elements, layout problems, or unexpected behavior.",
  },
  {
    icon: TrendingUp,
    title: "Performance Support",
    description:
      "Monitoring and improvements that help the site load and perform better.",
  },
  {
    icon: Smartphone,
    title: "Content and Page Updates",
    description:
      "Support for updating existing pages, adding small changes, and keeping the website current.",
  },
  {
    icon: Zap,
    title: "Technical Support",
    description:
      "Hands-on help when the website needs attention, troubleshooting, or follow-up fixes.",
  },
];

const offerings = [
  {
    title: "Routine Website Maintenance",
    description:
      "Ongoing support for updates, stability, and day-to-day website health.",
  },
  {
    title: "Technical Troubleshooting",
    description:
      "Help with site issues, broken functionality, and technical problems that need attention.",
  },
  {
    title: "Performance & Stability Support",
    description:
      "Maintenance focused on keeping the website fast, reliable, and usable.",
  },
  {
    title: "Post-Launch Support",
    description:
      "Support after launch to help the website stay aligned with business needs over time.",
  },
];

const deliverables = [
  "Regular update support",
  "Bug fixes and technical issue handling",
  "Basic performance monitoring",
  "Security-focused maintenance",
  "Page and content update support",
  "Troubleshooting when issues appear",
  "Technical review where needed",
  "Clear next-step recommendations",
];

const processSteps = [
  {
    step: "01",
    title: "Review",
    description:
      "We look at the current website setup, recent issues, and the kind of support the business actually needs.",
  },
  {
    step: "02",
    title: "Prioritize",
    description:
      "We identify which updates, fixes, or risks should be handled first.",
  },
  {
    step: "03",
    title: "Maintain",
    description:
      "We apply the needed updates, fixes, and improvements with a structured approach.",
  },
  {
    step: "04",
    title: "Monitor",
    description:
      "We keep an eye on performance, functionality, and technical health over time.",
  },
  {
    step: "05",
    title: "Support",
    description:
      "We respond to issues, content changes, and ongoing support needs as they come up.",
  },
  {
    step: "06",
    title: "Improve",
    description:
      "Where needed, we recommend improvements that support stronger performance and stability.",
  },
];

const faqs = [
  {
    question: "What is included in website maintenance?",
    answer: (
      <>
        Website maintenance can include updates, bug fixes, security support,
        performance checks, troubleshooting, and general technical upkeep
        depending on what the website needs.
      </>
    ),
    schemaAnswer:
      "Website maintenance can include updates, bug fixes, security support, performance checks, troubleshooting, and general technical upkeep depending on what the website needs.",
  },
  {
    question: "How often should a website be maintained?",
    answer: (
      <>
        A website should be reviewed regularly to keep updates current, reduce
        risk, and prevent avoidable technical issues from building up.
      </>
    ),
    schemaAnswer:
      "A website should be reviewed regularly to keep updates current, reduce risk, and prevent avoidable technical issues from building up.",
  },
  {
    question: "Can you maintain a website you did not build?",
    answer: (
      <>
        Yes. We can review an{" "}
        <Link
          to="/services/website-development"
          className="text-primary hover:underline"
        >
          existing website
        </Link>{" "}
        and support it based on its condition, setup, and maintenance
        requirements.
      </>
    ),
    schemaAnswer:
      "Yes. We can review an existing website and support it based on its condition, setup, and maintenance requirements.",
  },
  {
    question: "Do you also handle content updates?",
    answer: (
      <>
        Yes. We can support page updates, text changes, and smaller{" "}
        <Link
          to="/services/social-media-marketing"
          className="text-primary hover:underline"
        >
          content-related adjustments
        </Link>{" "}
        as part of ongoing website support.
      </>
    ),
    schemaAnswer:
      "Yes. We can support page updates, text changes, and smaller content-related adjustments as part of ongoing website support.",
  },
  {
    question: "Can maintenance help with SEO and performance?",
    answer: (
      <>
        Yes. Website maintenance can support{" "}
        <Link
          to="/services/seo-services"
          className="text-primary hover:underline"
        >
          SEO and usability
        </Link>{" "}
        by helping keep the site fast, stable, and technically healthy.
      </>
    ),
    schemaAnswer:
      "Yes. Website maintenance can support SEO and usability by helping keep the site fast, stable, and technically healthy.",
  },
  {
    question: "What happens if my website has a sudden issue?",
    answer: (
      <>
        We can help troubleshoot the issue, identify the cause, and apply the
        fix needed to get the website working properly again.
      </>
    ),
    schemaAnswer:
      "We can help troubleshoot the issue, identify the cause, and apply the fix needed to get the website working properly again.",
  },
];

const whyChooseUs = [
  {
    title: "Practical Ongoing Support",
    description:
      "We focus on the updates, fixes, and improvements that actually keep the website working well.",
  },
  {
    title: "Clear Technical Oversight",
    description:
      "We monitor the site with a structured approach instead of waiting for problems to become obvious.",
  },
  {
    title: "Built for Business Continuity",
    description:
      "Support work helps reduce disruption and keeps the website stable as the business grows.",
  },
  {
    title: "Connected to Your Wider Setup",
    description:
      "Website support works best when it considers SEO, structure, content, and future development needs too.",
  },
];

export default function MaintenanceSupportPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <Layout>
      <SEO
        title="Website Maintenance Support in Vancouver | Square Trigger"
        description="Get best-in-class Website support and maintenance services for optimal performance to provide 99.9% uptime of your Website. Request a Maintenance Quote now."
        ogTitle="Website Maintenance Support in Vancouver | Square Trigger"
        ogDescription="Get best-in-class Website support and maintenance services for optimal performance to provide 99.9% uptime of your Website. Request a Maintenance Quote now."
        keywords={SEO_KEYWORDS.maintenanceSupport}
      />

      {/* Hero Section */}
      <section className="pt-24 sm:pt-28 lg:pt-32 pb-16 sm:pb-18 lg:pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-foreground via-foreground/95 to-foreground/90" />
        <div className="absolute inset-0">
          <div className="absolute top-16 sm:top-20 right-6 sm:right-20 w-72 sm:w-96 h-72 sm:h-96 bg-primary/20 rounded-full blur-3xl" />
          <div className="absolute bottom-16 sm:bottom-20 left-6 sm:left-20 w-56 sm:w-72 h-56 sm:h-72 bg-secondary/20 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Badge className="mb-6 bg-primary/20 text-primary-foreground border-none">
                <Globe className="h-4 w-4 mr-2" />
                Our Services
              </Badge>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl sm:text-5xl lg:text-6xl font-display font-bold text-background mb-5 sm:mb-6 leading-tight"
            >
              Website Maintenance & Support in{" "}
              <span className="gradient-text">Vancouver</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-lg sm:text-xl text-background/70 mb-8 max-w-2xl mx-auto leading-relaxed"
            >
              We provide website maintenance and support services that help
              businesses keep their website secure, updated, fast, and working
              properly after launch.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center"
            >
              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>

              <Button
                variant="glass"
                size="lg"
                asChild
                className="w-full sm:w-auto justify-center text-white bg-white/10 border border-white/20 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] hover:-translate-y-[1px] transition-all duration-300"
              >
                <Link to="/contact">Contact Us</Link>
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Value Section */}
      <section className="pt-12 sm:pt-14 lg:pt-20 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-24 right-10 w-72 h-72 bg-secondary/5 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-5xl mx-auto text-center"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4">
              A Website Needs Ongoing Support to Keep{" "}
              <span className="gradient-text">Performing Well</span>
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-6">
              A{" "}
              <Link
                to="/services/website-development"
                className="text-primary hover:underline"
              >
                website
              </Link>{" "}
              is not finished the moment it goes live. Over time, updates,
              fixes, monitoring, and performance checks are needed to keep
              everything secure, stable, and easy to use.
            </p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-card/60 backdrop-blur rounded-2xl border border-border shadow-soft p-6 sm:p-8"
            >
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-3xl mx-auto mb-6">
                Website maintenance helps prevent avoidable problems, supports a
                better user experience, and gives your business more confidence
                that the website is working properly in the background.
              </p>

              <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
                {[
                  {
                    title: "Security updates",
                    desc: "Regular updates help reduce risk and keep the website more secure.",
                  },
                  {
                    title: "Performance checks",
                    desc: "Monitoring and maintenance help keep the website loading and functioning properly.",
                  },
                  {
                    title: "Bug fixes",
                    desc: "Small issues can quickly affect trust, usability, and conversion if they are not handled.",
                  },
                  {
                    title: "Content and system updates",
                    desc: "A supported website is easier to keep current as the business changes.",
                  },
                  {
                    title: "Long-term stability",
                    desc: "Good maintenance supports the long-term value of the website instead of letting problems build up.",
                  },
                ].map((item, i) => (
                  <motion.div
                    key={item.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.45, delay: 0.15 + i * 0.05 }}
                    className="rounded-xl border border-border bg-background/70 p-4 text-center hover:shadow-medium transition-shadow"
                  >
                    <div className="text-sm font-semibold text-foreground">
                      {item.title}
                    </div>
                    <div className="mt-1 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {item.title === "Bug fixes" ? (
                        <>
                          Small issues can quickly affect trust, usability, and{" "}
                          <Link
                            to="/services/website-development"
                            className="text-primary hover:underline"
                          >
                            conversion
                          </Link>{" "}
                          if they are not handled.
                        </>
                      ) : item.title === "Content and system updates" ? (
                        <>
                          A supported website is easier to keep{" "}
                          <Link
                            to="/services/social-media-marketing"
                            className="text-primary hover:underline"
                          >
                            content
                          </Link>{" "}
                          current as the business changes.
                        </>
                      ) : (
                        item.desc
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="pt-12 sm:pt-14 lg:pt-14 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-10"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground">
              Why Choose Square Trigger for Website Maintenance & Support
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground mt-4 leading-relaxed">
              We do not treat maintenance like an afterthought. Ongoing support
              is part of keeping a website useful, reliable, and aligned with
              the needs of the business over time.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="p-6 bg-card rounded-2xl border border-border shadow-soft hover:shadow-medium transition-shadow text-center sm:text-left flex flex-col items-center sm:items-start"
              >
                <div className="w-10 h-10 rounded-xl gradient-primary-soft flex items-center justify-center mb-4">
                  <div className="w-2.5 h-2.5 rounded-full bg-primary" />
                </div>

                <h3 className="font-semibold text-foreground mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {item.title === "Connected to Your Wider Setup" ? (
                    <>
                      Website support works best when it considers{" "}
                      <Link
                        to="/services/seo-services"
                        className="text-primary hover:underline"
                      >
                        SEO
                      </Link>
                      , structure,{" "}
                      <Link
                        to="/services/social-media-marketing"
                        className="text-primary hover:underline"
                      >
                        content
                      </Link>
                      , and future{" "}
                      <Link
                        to="/services/website-development"
                        className="text-primary hover:underline"
                      >
                        development
                      </Link>{" "}
                      needs too.
                    </>
                  ) : (
                    item.description
                  )}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-12 sm:mb-14"
          >
            <span className="text-sm font-medium text-primary uppercase tracking-wider">
              Capabilities
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
              What Our Maintenance & Support Service{" "}
              <span className="gradient-text">Includes</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {capabilities.map((capability, index) => {
              const Icon = capability.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.07 }}
                  className="h-full"
                >
                  <Card className="p-6 h-full border-border hover:border-primary/30 transition-all duration-300 card-hover text-center sm:text-left flex flex-col items-center sm:items-start">
                    <div className="w-12 h-12 rounded-xl gradient-primary flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-primary-foreground" />
                    </div>

                    <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-2">
                      {capability.title}
                    </h3>
                    <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                      {capability.title === "Performance Support" ? (
                        <>
                          Monitoring and improvements that help the site load
                          and perform better, with broader performance support
                          that can also connect to{" "}
                          <Link
                            to="/services/seo-services"
                            className="text-primary hover:underline"
                          >
                            SEO
                          </Link>
                          .
                        </>
                      ) : capability.title === "Content and Page Updates" ? (
                        <>
                          Support for updating existing pages, adding small
                          changes, and keeping the website current with{" "}
                          <Link
                            to="/services/social-media-marketing"
                            className="text-primary hover:underline"
                          >
                            content
                          </Link>{" "}
                          or page updates where needed.
                        </>
                      ) : (
                        capability.description
                      )}
                    </p>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* What We Offer */}
      <section className="section-padding bg-foreground text-background relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-10 w-80 h-80 bg-primary/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-secondary/10 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
          >
            <span className="text-sm font-medium text-secondary uppercase tracking-wider">
              Solutions
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold mt-3">
              Support Built Around{" "}
              <span className="gradient-text">Ongoing Website Needs</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {offerings.map((offering, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="bg-background/5 rounded-2xl p-6 sm:p-8 border border-background/10 hover:border-primary/30 transition-colors text-center sm:text-left flex flex-col items-center sm:items-start"
              >
                <h3 className="text-xl sm:text-2xl font-display font-bold mb-3">
                  {offering.title === "Post-Launch Support" ? (
                    <Link
                      to="/services/website-development"
                      className="hover:underline"
                    >
                      {offering.title}
                    </Link>
                  ) : (
                    offering.title
                  )}
                </h3>

                <p className="text-background/70 leading-relaxed">
                  {offering.title === "Performance & Stability Support" ? (
                    <>
                      Maintenance focused on keeping the website fast, reliable,
                      and usable, with support that can also strengthen{" "}
                      <Link
                        to="/services/seo-services"
                        className="text-primary hover:underline"
                      >
                        performance
                      </Link>{" "}
                      where natural.
                    </>
                  ) : (
                    offering.description
                  )}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Deliverables */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="text-center lg:text-left"
            >
              <span className="text-sm font-medium text-primary uppercase tracking-wider">
                Deliverables
              </span>

              <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3 mb-5">
                What You&apos;ll <span className="gradient-text">Receive</span>
              </h2>

              <p className="text-base sm:text-lg text-muted-foreground mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Our maintenance and support service is built around keeping your
                website working properly, reducing avoidable issues, and helping
                the business stay current online.
              </p>

              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="bg-card rounded-2xl p-6 sm:p-8 border border-border shadow-soft text-center sm:text-left"
            >
              <ul className="inline-grid sm:grid space-y-4 text-left">
                {deliverables.map((item, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: 14 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-start gap-3 w-fit"
                  >
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-[2px]" />
                    <span className="text-sm sm:text-base text-foreground/90 leading-relaxed">
                      {item === "Basic performance monitoring" ? (
                        <>
                          Basic{" "}
                          <Link
                            to="/services/seo-services"
                            className="text-primary hover:underline"
                          >
                            performance monitoring
                          </Link>
                        </>
                      ) : item === "Page and content update support" ? (
                        <>
                          Page and{" "}
                          <Link
                            to="/services/social-media-marketing"
                            className="text-primary hover:underline"
                          >
                            content update support
                          </Link>
                        </>
                      ) : item === "Technical review where needed" ? (
                        <>
                          <Link
                            to="/services/website-development"
                            className="text-primary hover:underline"
                          >
                            Technical review
                          </Link>{" "}
                          where needed
                        </>
                      ) : (
                        item
                      )}
                    </span>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="pt-12 sm:pt-14 lg:pt-12 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
          >
            <span className="text-sm font-medium text-primary uppercase tracking-wider">
              Our Process
            </span>
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
              How We Approach{" "}
              <span className="gradient-text">
                Website Maintenance & Support
              </span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {processSteps.map((step, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="relative h-full"
              >
                <Card className="p-6 h-full border-border hover:border-primary/30 transition-colors card-hover text-center sm:text-left flex flex-col items-center sm:items-start">
                  <div className="w-full mb-4 flex justify-center">
                    <div className="text-3xl font-display font-bold gradient-text text-center">
                      {step.step}
                    </div>
                  </div>

                  <h3 className="text-lg sm:text-xl font-display font-semibold text-foreground mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                    {step.title === "Monitor" ? (
                      <>
                        We keep an eye on{" "}
                        <Link
                          to="/services/seo-services"
                          className="text-primary hover:underline"
                        >
                          performance
                        </Link>
                        , functionality, and technical health over time.
                      </>
                    ) : step.title === "Support" ? (
                      <>
                        We respond to issues,{" "}
                        <Link
                          to="/services/social-media-marketing"
                          className="text-primary hover:underline"
                        >
                          content changes
                        </Link>
                        , and ongoing support needs as they come up.
                      </>
                    ) : step.title === "Improve" ? (
                      <>
                        Where needed, we recommend{" "}
                        <Link
                          to="/services/website-development"
                          className="text-primary hover:underline"
                        >
                          improvements
                        </Link>{" "}
                        that support stronger performance and stability.
                      </>
                    ) : (
                      step.description
                    )}
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mid CTA */}
      <section className="py-16 sm:py-20 gradient-primary relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-0 w-80 h-80 sm:w-96 sm:h-96 bg-background/10 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-80 h-80 sm:w-96 sm:h-96 bg-background/10 rounded-full blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-primary-foreground mb-4 sm:mb-6">
              Need Reliable Support for Your Website?
            </h2>

            <p className="text-base sm:text-lg text-primary-foreground/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              Let&apos;s look at what your website needs to stay secure, stable,
              and performing properly over time.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Button
                variant="premium"
                size="lg"
                asChild
                className="bg-background text-foreground hover:bg-background/90 w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </Button>

              <Button
                variant="glass"
                size="lg"
                asChild
                className="w-full sm:w-auto justify-center text-white bg-white/10 border border-white/20 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] hover:-translate-y-[1px] transition-all duration-300"
              >
                <Link to="/contact">Contact Us</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQs */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-10 sm:mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mt-3">
              Website Maintenance & Support –{" "}
              <span className="gradient-text">Frequently Asked Questions</span>
            </h2>
          </motion.div>

          <div className="max-w-3xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={faq.question}
                className="relative z-10 bg-card rounded-xl border border-border overflow-hidden"
              >
                <button
                  type="button"
                  onPointerUp={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 sm:p-6 text-left hover:bg-accent/50 transition-colors pointer-events-auto touch-manipulation"
                >
                  <span className="font-semibold text-foreground pr-6">
                    {faq.question}
                  </span>
                  <ChevronDown
                    className={`h-5 w-5 text-muted-foreground transition-transform duration-300 flex-shrink-0 ${
                      openFaq === index ? "rotate-180" : ""
                    }`}
                  />
                </button>

                <div
                  className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                    openFaq === index ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="overflow-hidden">
                    <div className="px-5 sm:px-6 pb-5 sm:pb-6 text-muted-foreground leading-relaxed">
                      {faq.answer}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqs.map((faq) => ({
                "@type": "Question",
                name: faq.question,
                acceptedAnswer: {
                  "@type": "Answer",
                  text: faq.schemaAnswer,
                },
              })),
            })}
          </script>
        </div>
      </section>

      {/* Final CTA */}
      <section className="pt-12 sm:pt-14 lg:pt-16 pb-6 sm:pb-8 lg:pb-10 bg-background relative overflow-hidden">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-foreground mb-4 sm:mb-6">
              Ready to Keep Your Website Running Properly?
            </h2>

            <p className="text-base sm:text-lg text-muted-foreground mb-8 leading-relaxed max-w-2xl mx-auto">
              Let&apos;s put the right maintenance and support in place so your
              website stays secure, current, and reliable.
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>

              <Button
                variant="outline"
                size="lg"
                asChild
                className="w-full sm:w-auto justify-center"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center"
                >
                  Contact Us
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}